import { Divider } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import ChipContainer from '../../../common/ChipContainer';
import FaroDeckLink from '../../../faro/decks/FaroDeckLink';
import FaroParametersTable from '../../../faro/parameters/FaroParametersTable';
import FaroDeploymentLink from '../../../faro/deployments/FaroDeploymentLink';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1
    },
    largeGutter: {
      marginBottom: 16,
    },
    tableCell: {
      borderBottom: 'none'
    },
    tableCellHeader: {
      borderBottom: 'none',
      paddingLeft: 0,
      maxWidth: 300,
      width: 300
    },
    topSection: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        '&>:first-child': {
          paddingRight: 8
        },
        '&>:nth-child(2)': {
          paddingLeft: 8
        }
      }
    },
    actionDetails: {
      flex: 1,
      marginBottom: 16,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    actionOptions: {
      flex: 1
    },
    section: {
      marginBottom: 24
    },
    paramSetTable: {
      tableLayout: 'fixed'
    }
  })
);

interface Props {
  action: any;
  accountId: string;
  region: string;
}

function WorkflowDeploymentActionDetails(props: Props) {
  const { action, accountId, region } = props;
  const classes = useStyles({});

  return (
    <div className={classes.root}>
      <div className={classes.topSection}>
        <div className={classes.actionDetails}>
          <Typography color="textSecondary">Details</Typography>
          <Divider light className={classes.largeGutter} />
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Deck Name</Typography>
            <FaroDeckLink
              variant="body1"
              deckName={action.DeckName}
              version={action.Version}
              tag={action.Tag}
            />
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Deck Identifier</Typography>
            <Typography color="textSecondary">{action.Tag || action.Version}</Typography>
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Deployment Name</Typography>
            <FaroDeploymentLink
              variant="body1"
              deploymentName={action.DeploymentName}
              accountId={accountId}
              region={region}
            />
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Dependencies</Typography>
            {action.Dependencies.length === 0 && <Typography color="textSecondary">None</Typography>}
            <ChipContainer>
              {action.Dependencies.map(dependency => <Chip key={dependency} label={dependency} variant="outlined" size="small" />)}
            </ChipContainer>
          </div>
          <div className={classes.largeGutter}>
            <Typography gutterBottom>Capability Flags</Typography>
            {((!action.CapabilityFlags || action.CapabilityFlags.length === 0) && !action.IAM) && <Typography color="textSecondary">None</Typography>}
            <ChipContainer>
              {action.CapabilityFlags && action.CapabilityFlags.map(item => <Chip key={item} label={item} variant="outlined" size="small" />)}
              {((!action.CapabilityFlags || action.CapabilityFlags.length === 0) && action.IAM) && <Chip label={'CAPABILITY_NAMED_IAM'} variant="outlined" size="small" />}
            </ChipContainer>
          </div>
        </div>
        <div className={classes.actionOptions}>
          <Typography color="textSecondary">Faro Options</Typography>
          <Divider light className={classes.largeGutter} />
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell className={classes.tableCellHeader}>
                  Update Existing Deployment
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={action.Update ? 'Yes' : 'No'}
                    color={action.Update ? 'primary' : 'default'}
                    size="small" variant={action.Update ? 'default' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellHeader}>
                  Skip Stacks
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={action.SkipStacks ? 'Yes' : 'No'}
                    color={action.SkipStacks ? 'primary' : 'default'}
                    size="small" variant={action.SkipStacks ? 'default' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellHeader}>
                  Global Deployment
                 </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={action.Global ? 'Yes' : 'No'}
                    color={action.Global ? 'primary' : 'default'}
                    size="small" variant={action.Global ? 'default' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellHeader}>
                  Disable Stack Rollback
                                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={action.DisableStackRollback ? 'Yes' : 'No'}
                    color={action.DisableStackRollback ? 'primary' : 'default'}
                    size="small" variant={action.DisableStackRollback ? 'default' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCellHeader}>
                  Ignore Existing Parameters
                                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Chip
                    label={action.IgnoreExistingParameters ? 'Yes' : 'No'}
                    color={action.IgnoreExistingParameters ? 'primary' : 'default'}
                    size="small" variant={action.IgnoreExistingParameters ? 'default' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
      <div className={classes.section}>
        <Typography color="textSecondary">IAM Roles</Typography>
        <Divider light className={classes.largeGutter} />
        <div className={classes.largeGutter}>
          <Typography>Deploy Role to Assume</Typography>
          <Typography color="textSecondary">{action.AssumeRoleARN || 'None'}</Typography>
        </div>
        <div className={classes.largeGutter}>
          <Typography>Teardown Role to Assume</Typography>
          <Typography color="textSecondary">{action.TeardownAssumeRoleARN || 'None'}</Typography>
        </div>
      </div>
      <div className={classes.section}>
        <Typography color="textSecondary">Parameters</Typography>
        <Divider light className={classes.largeGutter} />
        {(!action.Parameters || Object.keys(action.Parameters).length === 0) && <Typography>None</Typography>}
        {action.Parameters && Object.keys(action.Parameters).length > 0 &&
          <FaroParametersTable hideKmsKeyArn items={Object.keys(action.Parameters).map(key => ({ name: key, value: action.Parameters[key] }))} />}
      </div>
      <div>
        <Typography color="textSecondary">Parameter Sets</Typography>
        <Divider light className={classes.largeGutter} />
        {action.ParameterSets.length === 0 && <Typography>None</Typography>}
        {action.ParameterSets.length > 0 && <Table size="small" className={classes.paramSetTable}>
          <TableHead>
            <TableRow>
              <TableCell>Parameter Set Name</TableCell>
              <TableCell>Parameter Set Version</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {action.ParameterSets.map(paramSet => <TableRow key={paramSet.Name}>
              <TableCell>
                <Link
                  variant="body1"
                  target="_blank"
                  rel="noreferrer"
                  href={`/faro/parameters/${accountId}/${region}/${paramSet.Name}?version=${paramSet.Version}`}
                >
                  {paramSet.Name}
                </Link>
              </TableCell>
              <TableCell>{paramSet.Version}</TableCell>
            </TableRow>)}
          </TableBody>
        </Table>}
      </div>
    </div>
  );
};

export default WorkflowDeploymentActionDetails;
